import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Users,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  Filter,
  Sparkles,
  Loader2,
  Trash2,
  Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { exportToExcel, exportToPDF } from '@/lib/exam-export';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  not_started: { label: 'Non démarré', color: 'bg-gray-500' },
  in_progress: { label: 'En cours', color: 'bg-blue-500' },
  submitted: { label: 'Soumis', color: 'bg-yellow-500' },
  graded: { label: 'Corrigé', color: 'bg-green-500' },
  timed_out: { label: 'Temps écoulé', color: 'bg-red-500' },
};

export default function ExamAttemptsPage() {
  const { workspaceId, examId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  
  // Redirect candidates - they should not see all attempts
  useEffect(() => {
    if (!canManage) {
      navigate(`/workspace/${workspaceId}/exams`, { replace: true });
    }
  }, [canManage]);
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [gradingAttemptId, setGradingAttemptId] = useState(null);
  
  useEffect(() => {
    const init = async () => {
      if (workspaceId && examId) {
        await Promise.all([loadExam(workspaceId), loadAttempts(workspaceId)]);
      }
    };
    
    init();
  }, [workspaceId, examId]);
  
  const loadExam = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}?workspace_id=${wsId}&include_questions=false`);
      if (response.ok) {
        const data = await response.json();
        setExam(data);
      }
    } catch (error) {
      console.error('Failed to load exam:', error);
    }
  };
  
  const loadAttempts = async (wsId) => {
    try {
      let url = `${API_URL}/api/exams/attempts?workspace_id=${wsId}&exam_id=${examId}`;
      
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (resultFilter === 'passed') {
        url += `&passed=true`;
      } else if (resultFilter === 'failed') {
        url += `&passed=false`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAttempts(data);
      } else {
        toast.error('Erreur lors du chargement des tentatives');
      }
    } catch (error) {
      console.error('Failed to load attempts:', error);
      toast.error('Erreur lors du chargement des tentatives');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (workspaceId) {
      loadAttempts(workspaceId);
    }
  }, [statusFilter, resultFilter]);
  
  const handleAIGrading = async (attemptId) => {
    if (!workspaceId) {
      toast.error('Workspace non trouvé');
      return;
    }
    
    setGradingAttemptId(attemptId);
    try {
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attemptId}/ai-grade?workspace_id=${workspaceId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la correction IA');
      }
      
      const data = await response.json();
      
      if (data.questions_graded > 0) {
        toast.success(`${data.questions_graded} question(s) corrigée(s) par l'IA`);
        // Reload attempts to refresh the list
        await loadAttempts(workspaceId);
      } else if (data.questions_already_graded > 0) {
        toast.info('Toutes les questions ont déjà été corrigées');
      } else if (data.questions_skipped > 0) {
        toast.info('Aucune réponse à corriger (questions sans réponse)');
      } else {
        toast.info('Aucune question à corriger par l\'IA');
      }
    } catch (error) {
      console.error('AI grading error:', error);
      toast.error(error.message);
    } finally {
      setGradingAttemptId(null);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    // Filter attempts by user name (client-side)
  };

  const handleDeleteAttempt = async (attemptId) => {
    if (!window.confirm('Supprimer cette tentative ? Cette action est irréversible.')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attemptId}?workspace_id=${workspaceId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setAttempts(prev => prev.filter(a => a.id !== attemptId));
        toast.success('Tentative supprimée');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Supprimer toutes les ${filteredAttempts.length} tentative(s) affichée(s) ? Cette action est irréversible.`)) return;
    try {
      for (const attempt of filteredAttempts) {
        await fetch(
          `${API_URL}/api/exams/attempts/${attempt.id}?workspace_id=${workspaceId}`,
          { method: 'DELETE' }
        );
      }
      setAttempts(prev => prev.filter(a => !filteredAttempts.find(f => f.id === a.id)));
      toast.success('Toutes les tentatives supprimées');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      loadAttempts(workspaceId);
    }
  };
  
  const filteredAttempts = attempts.filter((attempt) => {
    if (!searchTerm) return true;
    return attempt.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
  }).sort((a, b) => {
    // Tri automatique par Note /20 (descendant)
    const maxA = a.max_score || a.total_points || 1;
    const earnedA = a.total_score ?? a.points_earned ?? 0;
    const noteA = (earnedA / maxA) * 20;
    const maxB = b.max_score || b.total_points || 1;
    const earnedB = b.total_score ?? b.points_earned ?? 0;
    const noteB = (earnedB / maxB) * 20;
    return noteB - noteA;
  });
  
  const calculateDuration = useCallback((attempt) => {
    if (!attempt.started_at || !attempt.submitted_at) return '-';
    const start = new Date(attempt.started_at);
    const end = new Date(attempt.submitted_at);
    const minutes = Math.round((end - start) / 60000);
    return `${minutes} min`;
  }, []);

  const handleExportExcel = async () => {
    if (filteredAttempts.length === 0) return;
    try {
      await exportToExcel(filteredAttempts, exam?.title || 'Examen', calculateDuration);
      toast.success('Export Excel téléchargé');
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const handleExportPDF = () => {
    if (filteredAttempts.length === 0) return;
    exportToPDF(filteredAttempts, exam?.title || 'Examen', calculateDuration);
    toast.success('Export PDF téléchargé');
  };
  
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </WorkspaceLayout>
    );
  }
  
  return (
    <WorkspaceLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="h-7 w-7 text-primary" />
                Tentatives
              </h1>
              <p className="text-muted-foreground">{exam?.title}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {filteredAttempts.length > 0 && (
              <>
                <Button variant="outline" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" onClick={handleExportExcel}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Excel
                </Button>
                <Button variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50" onClick={handleExportPDF}>
                  <FileText className="h-4 w-4 mr-2" />
                  PDF
                </Button>
                <Button variant="outline" className="text-red-500 border-red-300 hover:bg-red-50" onClick={handleDeleteAll}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer tout
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/stats`)}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Voir les statistiques
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-attempts"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="submitted">Soumis</SelectItem>
                  <SelectItem value="graded">Corrigé</SelectItem>
                  <SelectItem value="timed_out">Temps écoulé</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={resultFilter} onValueChange={setResultFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-result">
                  <SelectValue placeholder="Résultat" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les résultats</SelectItem>
                  <SelectItem value="passed">Réussi</SelectItem>
                  <SelectItem value="failed">Échoué</SelectItem>
                </SelectContent>
              </Select>
            </form>
          </CardContent>
        </Card>
        
        {/* Attempts Table */}
        <Card>
          <CardContent className="p-0">
            {filteredAttempts.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Aucune tentative</h3>
                <p className="text-muted-foreground">
                  Aucun candidat n'a encore passé cet examen
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidat</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="hidden md:table-cell">Durée</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Note /20</TableHead>
                    <TableHead>Résultat</TableHead>
                    <TableHead className="hidden md:table-cell">Infractions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttempts.map((attempt) => {
                    const status = statusLabels[attempt.status] || statusLabels.graded;
                    
                    return (
                      <TableRow key={attempt.id} data-testid={`attempt-row-${attempt.id}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{attempt.user_name || 'Utilisateur'}</div>
                            <div className="text-xs text-muted-foreground">{attempt.user_id}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {formatDate(attempt.started_at)}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {calculateDuration(attempt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {attempt.percentage?.toFixed(1) || 0}%
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {attempt.total_score} / {attempt.max_score}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const maxScore = attempt.max_score || attempt.total_points || 1;
                            const earned = attempt.total_score ?? attempt.points_earned ?? 0;
                            const note = (earned / maxScore) * 20;
                            const color = note >= 16 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                              : note >= 14 ? 'text-blue-700 bg-blue-50 border-blue-200'
                              : note >= 10 ? 'text-amber-700 bg-amber-50 border-amber-200'
                              : 'text-red-700 bg-red-50 border-red-200';
                            return (
                              <span className={`inline-block px-2.5 py-1 rounded-md text-sm font-bold border ${color}`}>
                                {note.toFixed(1)}/20
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {attempt.status === 'graded' || attempt.status === 'submitted' ? (
                            <Badge className={cn(
                              "text-white",
                              attempt.passed ? "bg-green-500" : "bg-red-500"
                            )}>
                              {attempt.passed ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Réussi
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Échoué
                                </>
                              )}
                            </Badge>
                          ) : (
                            <Badge className={status.color}>
                              {status.label}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {attempt.violations_count > 0 ? (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {attempt.violations_count}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* AI Grading Button - admin only */}
                            {user?.role === 'admin' && (attempt.status === 'submitted' || attempt.status === 'graded') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAIGrading(attempt.id)}
                                disabled={gradingAttemptId === attempt.id}
                                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                title="Corriger les questions ouvertes par l'IA"
                              >
                                {gradingAttemptId === attempt.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4" />
                                )}
                                <span className="ml-1 hidden sm:inline">IA</span>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/workspace/${workspaceId}/exams/results/${attempt.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Voir
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteAttempt(attempt.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        {/* Summary */}
        {filteredAttempts.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap justify-center gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold">{filteredAttempts.length}</div>
                  <div className="text-sm text-muted-foreground">Tentatives affichées</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {filteredAttempts.filter(a => a.passed).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Réussites</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {filteredAttempts.filter(a => !a.passed && (a.status === 'graded' || a.status === 'submitted')).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Échecs</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {filteredAttempts.reduce((sum, a) => sum + (a.violations_count || 0), 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total infractions</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Actions */}
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux détails de l'examen
          </Button>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
